import { matchesTaxFoldGame } from '@configs/officialTopupNew/paytypeGameRules';

/** 与卡片税费行高度展开时长一致，保证合计数字和行高一起结束 */
const TOTAL_TWEEN_MS = 320;

/** 只接受普通十进制，避免把带分组符的金额误解析后显示成错误合计 */
const parseAmount = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') return null;
  const normalized = value.replace(/,/g, '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const formatAmount = value => Number(value).toFixed(2);

/**
 * 卡片合计与底部合计共用的税费展开状态（需组件提供 taxFoldRealTotalPrice 计算属性）。
 */
export default {
  data() {
    return {
      taxExpanded: false,
      animatedTotalText: '',
      _totalTweenFrame: 0
    };
  },
  computed: {
    taxFoldCurLocation() {
      if (this.curLocationVal != null && this.curLocationVal !== '') {
        return this.curLocationVal;
      }
      return this.curLocation;
    },
    isTaxCollapsible() {
      const tax = +this.curTax;
      return (
        tax > 0 &&
        this.taxFoldCurLocation === 'US' &&
        matchesTaxFoldGame(this.gameId)
      );
    },
    taxFoldNumericTotalPrice() {
      return parseAmount(this.taxFoldRealTotalPrice);
    },
    taxFoldNumericTaxPrice() {
      return parseAmount(this.curTax);
    },
    taxFoldCollapsedTotalPrice() {
      if (
        this.taxFoldNumericTotalPrice == null ||
        this.taxFoldNumericTaxPrice == null
      ) {
        return null;
      }
      return this.taxFoldNumericTotalPrice - this.taxFoldNumericTaxPrice;
    },
    shownTotalPrice() {
      if (!this.isTaxCollapsible) return this.taxFoldRealTotalPrice;
      if (this.animatedTotalText !== '') return this.animatedTotalText;
      if (
        !this.taxExpanded &&
        this.taxFoldCollapsedTotalPrice != null
      ) {
        return formatAmount(this.taxFoldCollapsedTotalPrice);
      }
      return this.taxFoldRealTotalPrice;
    },
    isPretaxTotal() {
      return this.isTaxCollapsible && !this.taxExpanded;
    },
    showStaticTaxRow() {
      return this.isShowTax && !this.isTaxCollapsible;
    },
    taxFoldPriceInputs() {
      return [
        this.isTaxCollapsible,
        this.taxFoldNumericTotalPrice,
        this.taxFoldNumericTaxPrice
      ];
    }
  },
  watch: {
    taxFoldPriceInputs: {
      handler() {
        if (!this.isTaxCollapsible) {
          this.stopTaxFoldTotalTween();
          this.taxExpanded = false;
          return;
        }
        this.syncTaxFoldDisplayedTotal();
      },
      immediate: true
    },
    taxExpanded(expanded) {
      if (!this.isTaxCollapsible) return;
      const to = expanded
        ? this.taxFoldNumericTotalPrice
        : this.taxFoldCollapsedTotalPrice;
      const from =
        parseAmount(this.animatedTotalText) ??
        (expanded
          ? this.taxFoldCollapsedTotalPrice
          : this.taxFoldNumericTotalPrice);
      this.tweenTaxFoldTotal(from, to);
    }
  },
  beforeDestroy() {
    this.stopTaxFoldTotalTween();
  },
  methods: {
    stopTaxFoldTotalTween() {
      if (!this._totalTweenFrame) return;
      cancelAnimationFrame(this._totalTweenFrame);
      this._totalTweenFrame = 0;
    },
    paintTaxFoldTotal(value) {
      const amount = Number(value);
      if (value != null && Number.isFinite(amount)) {
        this.animatedTotalText = formatAmount(amount);
        return;
      }
      if (this.taxExpanded) {
        this.animatedTotalText = String(this.taxFoldRealTotalPrice ?? '');
        return;
      }
      if (this.taxFoldCollapsedTotalPrice != null) {
        this.animatedTotalText = formatAmount(this.taxFoldCollapsedTotalPrice);
        return;
      }
      this.animatedTotalText = String(this.taxFoldRealTotalPrice ?? '');
    },
    tweenTaxFoldTotal(from, to) {
      this.stopTaxFoldTotalTween();
      if (from == null || to == null || from === to) {
        this.paintTaxFoldTotal(to);
        return;
      }
      const startAt = performance.now();
      const tick = now => {
        const progress = Math.min(1, (now - startAt) / TOTAL_TWEEN_MS);
        const eased = 1 - (1 - progress) ** 3;
        this.paintTaxFoldTotal(from + (to - from) * eased);
        if (progress < 1) {
          this._totalTweenFrame = requestAnimationFrame(tick);
          return;
        }
        this._totalTweenFrame = 0;
        this.paintTaxFoldTotal(to);
      };
      this._totalTweenFrame = requestAnimationFrame(tick);
    },
    syncTaxFoldDisplayedTotal() {
      this.stopTaxFoldTotalTween();
      const target = this.taxExpanded
        ? this.taxFoldNumericTotalPrice
        : this.taxFoldCollapsedTotalPrice;
      this.paintTaxFoldTotal(target);
    },
    expandTaxDetail() {
      if (!this.isTaxCollapsible || this.taxExpanded) return;
      this.taxExpanded = true;
    },
    collapseTaxDetail() {
      if (!this.taxExpanded) return;
      this.taxExpanded = false;
    }
  }
};
